import type {ErrorType, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {handleErrorAndGetErrorMessage} from '@Libraries/ai-text-prompt-box-library';
import {reuseOrUseGeneratedContent} from '@Components/ai-writer/ai-writer.library';
import type {EditableComponentsMap} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {EditableComponent} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';
import {getBackgroundImageComponents, getDesignComponents, getImageComponents, ImageComponentType} from '@Libraries/email-campaign-ai-image-regeneration-library';
import type {Editor} from 'grapesjs';

export const EMAIL_CAMPAIGN_AI_CONTENT_API_PATH = 'emailcampaignaicontent';
export const EMAIL_CAMPAIGN_AI_REDESIGN_TEXT_GENERATION_COST = 1;

const SUBJECT_USED_KEYS_SESSION_STORAGE_KEY = 'email-campaign-writer-subjects-used-keys';
const PREVIEW_USED_KEYS_SESSION_STORAGE_KEY = 'email-campaign-writer-previews-used-keys';

export const IMAGE_TYPE_COST: Record<ImageComponentType, number> = {
  [ImageComponentType.IMAGE]: 1,
  [ImageComponentType.BACKGROUND_IMAGE]: 1,
  [ImageComponentType.DESIGN]: 2,
};

interface UsedEmailContentResponse {
  contentGroupKey: string;
}

interface PersistUsedEmailContentParams {
  storageKey: string;
  lookupKey: string;
  contentGroupKey?: string;
  reuseEndpoint: string;
  reuseKeyName: string;
  useEndpoint: string;
  usePayload: Record<string, unknown>;
}

export interface PersistUsedEmailSubjectLineParams {
  entityId: string;
  entityType: string;
  prompt: string;
  subject: string;
  previewText: string;
  configOptions: SelectedValueType;
  contentGroupKey?: string;
}

export interface PersistUsedEmailPreviewTextParams {
  entityId: string;
  entityType: string;
  prompt: string;
  previewText: string;
  configOptions: SelectedValueType;
  contentGroupKey?: string;
}

const persistUsedEmailContent = async ({
  storageKey,
  lookupKey,
  contentGroupKey,
  reuseEndpoint,
  reuseKeyName,
  useEndpoint,
  usePayload,
}: PersistUsedEmailContentParams): Promise<void> => {
  await reuseOrUseGeneratedContent({
    storageKey,
    lookupKey,
    existingKey: contentGroupKey,
    reuse: async (storedKey: string): Promise<void> => {
      await window.PMW.writeLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/${reuseEndpoint}`, {[reuseKeyName]: storedKey});
    },
    use: async (): Promise<string> => {
      const response = (await window.PMW.writeLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/${useEndpoint}`, usePayload)) as UsedEmailContentResponse;
      return response.contentGroupKey;
    },
  });
};

export const persistUsedEmailSubjectLine = async ({
  entityId,
  entityType,
  prompt,
  subject,
  previewText,
  configOptions,
  contentGroupKey,
}: PersistUsedEmailSubjectLineParams): Promise<void> => {
  await persistUsedEmailContent({
    storageKey: SUBJECT_USED_KEYS_SESSION_STORAGE_KEY,
    lookupKey: subject,
    contentGroupKey,
    reuseEndpoint: 'reuseAiEmailSubjectLine',
    reuseKeyName: 'contentGroupKeyForEmailSubjectLine',
    useEndpoint: 'useEmailSubjectLine',
    usePayload: {prompt, subject, preview_text: previewText, configOptions, entityId, entityType},
  });
};

export const persistUsedEmailPreviewText = async ({
  entityId,
  entityType,
  prompt,
  previewText,
  configOptions,
  contentGroupKey,
}: PersistUsedEmailPreviewTextParams): Promise<void> => {
  await persistUsedEmailContent({
    storageKey: PREVIEW_USED_KEYS_SESSION_STORAGE_KEY,
    lookupKey: previewText,
    contentGroupKey,
    reuseEndpoint: 'reusePreviewText',
    reuseKeyName: 'contentGroupKey',
    useEndpoint: 'usePreviewText',
    usePayload: {prompt, preview_text: previewText, configOptions, entityId, entityType},
  });
};

export enum GenerateIdeasOriginator {
  Generate = 'generate',
  MoreIdeas = 'more_ideas',
  Alter = 'alter',
}


export enum EmailEditorAiPromptBoxVariant {
  TEMPLATE_EDITOR = 'template_editor',
  SINGLE_TEXT_EDITOR = 'single_text_editor',
  SINGLE_TEXT_EDITOR_DROPDOWN = 'single_text_editor_dropDown',
}

export enum EmailMakerAiRewriteMode {
  REWRITE_ALL_TEXT_COMPONENTS = 'rewrite_all_text_components',
  REWRITE_SELECTED_TEXT_COMPONENT = 'rewrite_single_text_component',
}

export enum EmailCampaignAiRedesignSource {
  EMAIL_MAKER_AI_REDESIGN = 'email_maker_ai_redesign',
}

export enum EmailCampaignAiRewriteSource {
  EMAIL_MAKER_AI_REDESIGN = 'email_maker_ai_redesign',
  EMAIL_MAKER_AI_REWRITE = 'email_maker_ai_rewrite',
  EMAIL_MAKER_AI_REWRITE_DROPDOWN = 'email_maker_ai_rewrite_dropdown',
}

export enum EmailCampaignAiRegenerateImageSource {
  EMAIL_MAKER_AI_REDESIGN = 'email_maker_ai_redesign',
}

export const getAiContentErrorMessage = (error: ErrorType): string | null => {
  return handleErrorAndGetErrorMessage(error);
};

export function useGetRemainingCreditsForEmailCampaignAiContent(): number {
  const teamCredits = useGetTeamCredits();
  return teamCredits ?? 0;
}

export function getTextCount(editor: Editor): number {
  return editor.getWrapper()?.find(`[data-pmw-type="${window.PMW.emails.components.text.PMW_TEXT}"]`).length ?? 0;
}

export function getImageTypeCounts(editor: Editor): Record<ImageComponentType, number> {
  return {
    [ImageComponentType.IMAGE]: getImageComponents(editor).length,
    [ImageComponentType.BACKGROUND_IMAGE]: getBackgroundImageComponents(editor).length,
    [ImageComponentType.DESIGN]: getDesignComponents(editor).length,
  };
}

export function getAiRedesignCost(editableComponentMap: EditableComponentsMap, imageTypeCounts: Record<ImageComponentType, number>, textCount: number): number {
  let cost = 0;

  for (const [componentTypeStr, isEnabled] of Object.entries(editableComponentMap)) {
    if (!isEnabled) continue;

    const componentType = componentTypeStr as EditableComponent;

    switch (componentType) {
      case EditableComponent.TEXT:
        if (textCount > 0) {
          cost += EMAIL_CAMPAIGN_AI_REDESIGN_TEXT_GENERATION_COST;
        }
        break;
      case EditableComponent.IMAGE:
        cost += getMaxImageRegenerationCost(imageTypeCounts);
        break;
      default:
        break;
    }
  }

  return cost;
}

function getMaxImageRegenerationCost(imageTypeCounts: Record<ImageComponentType, number>): number {
  let cost = 0;
  for (const [typeStr, count] of Object.entries(imageTypeCounts)) {
    cost += IMAGE_TYPE_COST[typeStr as ImageComponentType] * count;
  }
  return cost;
}
